import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { ConfigService } from '../../../core/services/config/config.service';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { UtilitiesService } from '../../../core/services/utilities.service';
import { ZorroTableComponent } from '../../../components/common/custom-table/zorro-table.component';
import { MaterialModule } from '../../../core/shared/material.module';
import { MessageEnum } from '../../../core/utils/message.enum';
import { MultiSegmentProgressBarComponent, ProgressSegment } from '../../common/multi-segment-progress-bar/multi-segment-progress-bar.component';
import { StatusBadgeComponent } from '../../common/status-badge/status-badge.component';
import { LogsModalComponent } from '../../modal-templates/logs-modal/logs-modal.component';
import { SelfHealedObjectsModalComponent } from '../../modal-templates/self-healed-objects-modal/self-healed-objects-modal.component';
import { ExecutionCycle } from '../execution-cycle-card/execution-cycle-card.component';

interface ExecutionPlanRow {
  id: string;
  name: string;
  status: string;
  startTime?: string;
  endTime?: string;
  totalCount: number;
  completedCount: number;
  executingCount: number;
  pendingCount: number;
  expanded: boolean;
  selected: boolean;
  raw: any;
}

@Component({
  selector: 'app-execution-cycle-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, NzPaginationModule, StatusBadgeComponent, MultiSegmentProgressBarComponent, ZorroTableComponent, MaterialModule],
  templateUrl: './execution-cycle-detail.component.html',
  styleUrl: './execution-cycle-detail.component.scss',
})
export class ExecutionCycleDetailComponent implements OnChanges {
  private readonly modalService    = inject(NgbModal);
  private readonly restService     = inject(RestService);
  private readonly toaster         = inject(ToasterService);
  private readonly utilityService  = inject(UtilitiesService);
  private readonly configService   = inject(ConfigService);

  /** The execution cycle whose details should be displayed. */
  @Input() cycle!: ExecutionCycle;

  /** Passed down from ExecutionOverviewViewComponent */
  @Input() projectId: string = '';
  @Input() automationProjectId: string = '';

  rows: ExecutionPlanRow[] = [];
  currentPage: number = 1;
  pageSize: number = 5;

  /** Options passed to every nested app-zorro-table */
  readonly defaultTableOptions: any = {
    pageSize: 10000,
    showPagination: false,
    showPageSizeChanger: false,
    showSelection: false,
    expandable: false,
  };

  /** Column definitions for the nested test-case table */
  readonly testCaseTableColumns: any[] = [
    {
      display_name: 'Test Case',
      key: 'testCaseName',
      headerStyles: { background: '#1B1F2E', color: '#ffffff', width: '24%' },
      _config: {
        type: 'copy_id',
        key: 'testCaseId',
        value: 'content_copy',
        tooltip: 'Copy the Execution ID',
        tooltipIcon: 'help',
        isClickable: 'true',
      },
    },
    {
      display_name: 'Start Time',
      key: 'startTime',
      headerStyles: { background: '#1B1F2E', color: '#ffffff', width: '14%' },
    },
    {
      display_name: 'End Time',
      key: 'endTime',
      headerStyles: { background: '#1B1F2E', color: '#ffffff', width: '14%' },
    },
    {
      display_name: 'Status',
      key: 'status',
      headerStyles: { background: '#1B1F2E', color: '#ffffff', width: '12%' },
      _config: {
        type: 'lozenge_status',
        classMapping: {
          WAITING:   'waiting-class status-card',
          PENDING:   'waiting-class status-card',
          ABORTED:   'aborted-class status-card',
          EXITED:    'aborted-class status-card',
          SKIPPED:   'aborted-class status-card',
          STOPPED:   'aborted-class status-card',
          EXECUTING: 'executing-class status-card',
          PASS:      'passed-class status-card',
          PASSED:    'passed-class status-card',
          FAIL:      'failed-class status-card',
          FAILED:    'failed-class status-card',
        },
      },
    },
    {
      display_name: 'Execution Engine',
      key: 'agentType',
      headerStyles: { background: '#1B1F2E', color: '#ffffff', width: '14%' },
      _config: {
        type: 'image_with_text',
        prefixImage: {
          targetKey: 'agentType',
          values: {
            LOCAL:   'assets/images/virtusa-icons/local-agent.svg',
            CLUSTER: 'assets/images/virtusa-icons/cluster-agent.svg',
          },
          styles: {
            LOCAL:   'width:18px;height:18px;margin-right:6px;',
            CLUSTER: 'width:18px;height:18px;margin-right:6px;',
          },
        },
      },
    },
    {
      display_name: 'Self-healed Objects',
      key: 'selfHealed',
      headerStyles: { background: '#1B1F2E', color: '#ffffff', width: '14%' },
      cellStyles: { color: '#5C36A2', cursor: 'pointer', fontWeight: '600' },
      _config: {
        type: 'link',
        displayInColumn: false,
      },
    },
    {
      display_name: 'Logs',
      key: 'logsLabel',
      headerStyles: { background: '#1B1F2E', color: '#ffffff', width: '6%', borderRadius: '0 6px 0 0' },
      _config: {
        type: 'icon',
        actionType: 'open_logs',
        isCondional: false,
        isClickable: 'true',
        displayWithText: true,
        value: 'article',
        iconStyles: { color: '#5C36A2', fontSize: '20px', cursor: 'pointer', marginRight: '4px' },
      },
    },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cycle']) {
      this.currentPage = 1;
      this.rows = this.buildRows();
    }
  }

  get paginatedRows(): ExecutionPlanRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.rows.slice(start, start + this.pageSize);
  }

  get totalItems(): number {
    return this.rows.length;
  }

  get showingStart(): number {
    if (!this.totalItems) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  toggleExpanded(row: ExecutionPlanRow, event?: Event): void {
    event?.stopPropagation();
    row.expanded = !row.expanded;
  }

  onRowChecked(row: ExecutionPlanRow, event: Event): void {
    event.stopPropagation();
    row.selected = (event.target as HTMLInputElement).checked;
  }

  downloadReport(row: ExecutionPlanRow, event: Event): void {
    event.stopPropagation();
    const planId = row.raw?.id ?? row.id;
    this.restService
      .downloadExecutionReport(this.projectId, this.automationProjectId, planId, 'PLAN')
      .subscribe({
        next: (res: any) => {
          const url = res?.data ?? res?.url;
          if (url) {
            window.open(url, '_blank');
          } else {
            this.toaster.pop({ type: 'error', text: MessageEnum.PROJECT_DOWNLOAD_FAILURE });
          }
        },
        error: () => {
          this.toaster.pop({ type: 'error', text: MessageEnum.PROJECT_DOWNLOAD_FAILURE });
        },
      });
  }

  openInsights(row: ExecutionPlanRow, event: Event): void {
    event.stopPropagation();
    const reportUrl = this.configService.getConfig("reportUrl");
    if (!reportUrl) return;

    const tenantId            = encodeURIComponent(this.utilityService.extractRealmFromUrl() ?? 'null');
    const projectId           = encodeURIComponent(this.projectId);
    const automationProjectId = encodeURIComponent(this.automationProjectId);
    const reportId            = encodeURIComponent(row.raw?.id ?? row.id);
    const type                = 'PLAN';

    const url = `${reportUrl}?tenantId=${tenantId}&projectId=${projectId}&automationProjectId=${automationProjectId}&reportId=${reportId}&type=${type}`;
    window.open(url, '_blank');
  }

  trackByRowId(_index: number, row: ExecutionPlanRow): string {
    return row.id;
  }

  getRowSegments(row: ExecutionPlanRow): ProgressSegment[] {
    const total = row.totalCount || 1;
    const toPercent = (n: number) => Math.round((n / total) * 100);
    return [
      { label: `Completed ${row.completedCount}`, percentage: toPercent(row.completedCount), color: '#5F2CFF' },
      { label: `Executing ${row.executingCount}`,  percentage: toPercent(row.executingCount),  color: '#DED6FF' },
      { label: `Pending ${row.pendingCount}`,      percentage: toPercent(row.pendingCount),     color: '#E8E8E9' },
    ].filter(s => s.percentage > 0);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  }


  getDurationLabel(row: ExecutionPlanRow): string {
    if (!row.startTime || !row.endTime) return '—';

    const start = new Date(row.startTime).getTime();
    const end = new Date(row.endTime).getTime();
    const diff = end - start;
    if (diff < 0) return '—';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  }

  /** Maps the raw BE test-case items nested inside a plan row into zorro-table rows. */
  getTestCaseTableRows(row: ExecutionPlanRow): any[] {
    let children: any[] = [];
    if (Array.isArray(row.raw?.testCases)) {
      children = row.raw.testCases;
    } else if (Array.isArray(row.raw?.children)) {
      children = row.raw.children;
    }

    return children.map((item: any, idx: number) => ({
      id:            String(item?.id ?? item?.executionId ?? `tc-${idx}`),
      testCaseName:  item?.automationScriptName ?? item?.testCaseName ?? item?.name ?? `Test Case ${idx + 1}`,
      testCaseId:    String(item?.id ?? item?.testCaseId ?? '—'),
      startTime:     this.formatDate(item?.startTime),
      endTime:       this.formatDate(item?.endTime),
      status:        item?.status ?? 'PENDING',
      agentType:     (item?.agentType ?? item?.executionEngine ?? 'LOCAL').toUpperCase(),
      selfHealed:    item?.selfHealedObject?.length
                       ? `${item.selfHealedObject.length} Objects`
                       : '—',
      selfHealedObject: item?.selfHealedObject ?? [],
      // logsLabel:     'Logs',
      isDownloadable: 'true',
    }));
  }

  onTableRowSelect(_event: any, _row: ExecutionPlanRow): void {
    // Reserved for future row-selection logic inside the nested table.
  }

  handleTableActions(event: any, _row: ExecutionPlanRow): void {
    const { type, data } = event ?? {};

    if (type === 'copy_id') {
      const id: string = data?.row?.testCaseId ?? '';
      navigator.clipboard.writeText(id).catch(() => {});
      return;
    }

    if (type === 'link') {
      // Self-healed objects modal
      const modalRef = this.modalService.open(SelfHealedObjectsModalComponent, {
        centered: true,
        size: 'xl',
        windowClass: 'add-item-modal',
        backdrop: 'static',
      });
      modalRef.componentInstance.data = data?.row?.selfHealedObject ?? [];
      modalRef.componentInstance.executionInstanceTaskId = data?.row?.id ?? '';
    }

    if (type === 'icon' && data?.columnConfig?.actionType === 'open_logs') {
      const taskInstanceId: string = data?.row?.id ?? '';
      this.restService
        .getExecutionLogs(this.projectId, this.automationProjectId, taskInstanceId)
        .subscribe({
          next: (res: any) => {
            const modalRef = this.modalService.open(LogsModalComponent, {
              centered: true,
              size: 'xl',
              backdrop: 'static',
            });
            modalRef.componentInstance.configs = {
              title: 'Logs',
              data: res?.data?.logs ?? '',
            };
          },
          error: () => {
            this.toaster.pop({ type: 'error', text: MessageEnum.FETCH_LOGS_FAILURE });
          },
        });
    }
  }

  private buildRows(): ExecutionPlanRow[] {
    const children = Array.isArray(this.cycle?.children) ? this.cycle.children : [];

    // Fallback: if no child plans are available, render the selected cycle as one row.
    const source = children.length ? children : [this.cycle];

    return source.map((item: any, index: number) => {
      const executingCount = this.toCount(item?.executingCount ?? item?.runningCount ?? item?.inProgressCount);

      let completedCount = this.toCount(item?.completedCount);
      if (completedCount === 0) {
        completedCount =
          this.toCount(item?.passedCount) +
          this.toCount(item?.failedCount) +
          this.toCount(item?.abortedCount) +
          this.toCount(item?.stoppedCount);
      }

      let pendingCount = this.toCount(item?.pendingCount ?? item?.waitingCount ?? item?.queuedCount);
      let totalCount = this.toCount(item?.totalCount ?? item?.testCaseCount ?? item?.testcaseCount);

      if (totalCount === 0) {
        totalCount = completedCount + executingCount + pendingCount;
      }

      if (pendingCount === 0 && totalCount > completedCount + executingCount) {
        pendingCount = totalCount - completedCount - executingCount;
      }

      if (totalCount < completedCount + executingCount + pendingCount) {
        totalCount = completedCount + executingCount + pendingCount;
      }

      const id = String(item?.id ?? item?.executionId ?? item?._id ?? `row-${index}`);

      return {
        id,
        name: item?.automationTestPlanName,
        status: item?.status ?? item?.executionState ?? 'PENDING',
        startTime: item?.startTime,
        endTime: item?.endTime,
        totalCount,
        completedCount,
        executingCount,
        pendingCount,
        expanded: false,
        selected: false,
        raw: item,
      };
    });
  }

  private toCount(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return parsed;
  }
}
